/* __next_internal_client_entry_do_not_use__ ChangelogDialog auto */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { SparklesIcon } from "__barrel_optimize__?names=SparklesIcon!=!lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { changelog, hasUnreadChangelog, markChangelogSeen } from "@/lib/changelog";
export function ChangelogDialog() {
    const [open, setOpen] = useState(false);
    const [unread, setUnread] = useState(false);
    // hasUnreadChangelog() touches localStorage — only safe in an
    // effect so SSR and the client first render agree (no hydration
    // mismatch on the indicator dot).
    useEffect(()=>{
        setUnread(hasUnreadChangelog());
    }, []);
    function handleOpenChange(next) {
        setOpen(next);
        if (next && unread) {
            markChangelogSeen();
            setUnread(false);
        }
    }
    return /*#__PURE__*/ _jsxs(Dialog, {
        open: open,
        onOpenChange: handleOpenChange,
        children: [
            /*#__PURE__*/ _jsx(DialogTrigger, {
                asChild: true,
                children: /*#__PURE__*/ _jsxs(Button, {
                    variant: "ghost",
                    size: "sm",
                    className: "relative gap-2",
                    "aria-label": "What's new",
                    children: [
                        /*#__PURE__*/ _jsx(SparklesIcon, {
                            className: "w-4 h-4"
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            className: "hidden sm:inline",
                            children: "What's new"
                        }),
                        unread && /*#__PURE__*/ _jsx("span", {
                            "aria-hidden": true,
                            className: "absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-primary ring-2 ring-card"
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs(DialogContent, {
                className: "sm:max-w-2xl max-h-[85vh] flex flex-col p-0",
                children: [
                    /*#__PURE__*/ _jsxs(DialogHeader, {
                        className: "px-6 pt-6 pb-4 border-b border-border",
                        children: [
                            /*#__PURE__*/ _jsx(DialogTitle, {
                                children: "What's new"
                            }),
                            /*#__PURE__*/ _jsx(DialogDescription, {
                                children: "Recent improvements to the PDF\xa0→\xa0JRXML converter."
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(ScrollArea, {
                        className: "flex-1 px-6 py-4",
                        children: /*#__PURE__*/ _jsx("ol", {
                            className: "space-y-10",
                            children: changelog.map((entry)=>/*#__PURE__*/ _jsxs("li", {
                                    className: "space-y-3",
                                    children: [
                                        /*#__PURE__*/ _jsxs("header", {
                                            children: [
                                                /*#__PURE__*/ _jsx("h3", {
                                                    className: "text-2xl font-semibold text-foreground",
                                                    children: entry.version
                                                }),
                                                /*#__PURE__*/ _jsx("time", {
                                                    dateTime: entry.date,
                                                    className: "text-sm text-muted-foreground",
                                                    children: formatDate(entry.date)
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx("h4", {
                                            className: "text-sm font-medium text-foreground",
                                            children: "What's Changed"
                                        }),
                                        /*#__PURE__*/ _jsx("ul", {
                                            className: "list-disc pl-5 space-y-1.5 text-sm text-foreground marker:text-muted-foreground",
                                            children: entry.changes.map((change, idx)=>/*#__PURE__*/ _jsx("li", {
                                                    className: "leading-relaxed",
                                                    children: change
                                                }, idx))
                                        })
                                    ]
                                }, entry.tag))
                        })
                    })
                ]
            })
        ]
    });
}
function formatDate(iso) {
    // Intl on the locale-default produces something like "Jun 2, 2026"
    // which matches the marketing-style readability of the rest of the
    // page. Fallback to the raw ISO if Intl is unavailable.
    try {
        return new Intl.DateTimeFormat(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric"
        }).format(new Date(iso));
    } catch (e) {
        return iso;
    }
}
